<template>
    <input
        ref="inputRef"
        v-model="value"
        type="number"
        :min="min"
        :max="max"
        :step="step"
        :class="isInvalid ? invalidClass : null"
        @focus="onFocus"
        @blur="onBlur"
    />
</template>

<script setup lang="ts">
import { getDecimalPlaces, roundToNearestIncrement } from '~/utils/helpers';

const inputRef = ref<HTMLInputElement | null>();

const emit = defineEmits(['update:model-value', 'update:valid']);

const props = withDefaults(
    defineProps<{
        allowEmpty?: boolean;
        emitInvalid?: boolean;
        max?: number;
        min?: number;
        modelValue?: number;
        step?: number;
        updateWhenFocused?: boolean;
        invalidClass?: string;
    }>(),
    {
        allowEmpty: false,
        emitInvalid: false,
        max: undefined,
        min: undefined,
        modelValue: undefined,
        step: 1,
        updateWhenFocused: false,
        invalidClass: undefined,
    },
);

const value = ref<number | string | null>(
    undefined === props.modelValue ? null : props.modelValue,
);

const sanitizedValue = computed<number | null>(() => {
    if (value.value === '' && props.allowEmpty) {
        return null;
    }

    let sanitized = parseFloat(value.value as string);

    if (Number.isNaN(sanitized)) {
        if (props.allowEmpty) {
            return null;
        }

        sanitized = 0;
    }

    if (!props.emitInvalid) {
        if (props.min !== undefined && sanitized < props.min) {
            sanitized = props.min;
        }

        if (props.max !== undefined && sanitized > props.max) {
            sanitized = props.max;
        }

        if (sanitized % props.step !== 0) {
            sanitized = roundToNearestIncrement(sanitized, props.step);
        }
    }

    return sanitized;
});

const isInvalid = computed<boolean>(() => {
    if (props.allowEmpty && !value.value) {
        return false;
    }

    const valueFloat = parseFloat(value.value as string);

    return valueFloat !== sanitizedValue.value;
});

watch(isInvalid, (isInvalidValue) => {
    emit('update:valid', !isInvalidValue);
});

const emitQuantity = () => {
    if (sanitizedValue.value !== props.modelValue) {
        emit('update:model-value', sanitizedValue.value);
    }
};

watch(
    () => props.modelValue,
    (modelValue) => {
        const isFocused = document.activeElement === inputRef.value;

        if (props.updateWhenFocused || !isFocused) {
            value.value = modelValue;
        }
    },
);

watch(sanitizedValue, () => {
    emitQuantity();
});

const onFocus = () => {
    if (sanitizedValue.value === 0 || sanitizedValue.value === null) {
        value.value = '';
    }
};

const onBlur = (event: Event) => {
    /*
    This is needed because the browser will allow the user to enter invalid values like "1e", which
    the browser will report to Vue as "". This invalid value can't be overridden by setting the
    field's value to "". Directly checking the validity status of the field and setting a new value
    to something other than "" is the only way to clear out the invalid value.
    */
    if ((event.target as HTMLInputElement).validity.badInput) {
        value.value = props.min || 0;

        if (props.allowEmpty) {
            nextTick(() => {
                value.value = '';
            });
        }
    } else {
        value.value = sanitizedValue.value;
    }

    const stepDecimalPlaces = getDecimalPlaces(props.step);
    if (value.value && stepDecimalPlaces > 0) {
        value.value = value.value.toFixed(stepDecimalPlaces);
    }
};

const focus = () => {
    inputRef.value?.focus();
};

defineExpose({ focus });
</script>
